import React from "react";
import styles from "./FieldLabelStyler.module.css";
import toolbarStyles from "../../CygnusFormBuilderToolbar.module.css";
import { FieldProps } from "../../../../../InputFieldsController/InputFieldsController.utils";
import { Row } from "react-bootstrap";
import Select from "react-select";

interface FieldLabelStylerProps {
  selectedField: FieldProps;
  updateField: (updated: Partial<FieldProps>) => void;
}

/**
 * `FieldLabelStyler` provides UI controls for styling a field's label.
 * Includes bold, italic, underline, strikethrough, font color, and font size options.
 *
 * @component
 * @example
 * <FieldLabelStyler field={field} updateField={updateField} />
 */
const FieldLabelStyler: React.FC<FieldLabelStylerProps> = ({
  selectedField,
  updateField,
}) => {
  const toggleStyle = (
    styleKey: keyof NonNullable<FieldProps["textStyle"]>
  ) => {
    updateField({
      textStyle: {
        ...selectedField.textStyle,
        [styleKey]: !selectedField.textStyle?.[styleKey],
      },
    });
  };

  /**
   * Renders a single numeric margin input field for a given side.
   *
   * @param label - Label text shown above the input.
   * @param id - Identifier used as both input ID and side key.
   * @param onChange - Function to call when value changes.
   * @param value - Current value of the margin for this side.
   */
  const renderLabelMarginInput = (
    label: string,
    id: string,
    value?: number
  ) => (
    <div className={styles.customNumberInputWrapper}>
      <label style={{ fontWeight: 500, fontSize: 8 }}>{label}</label>
      <div className={toolbarStyles.customNumberInput}>
        <button
          className={toolbarStyles.decrement}
          onClick={() => {
            const newVal = (value ?? 0) - 1;
            if (newVal >= 0) {
              updateField({
                textStyle: {
                  ...selectedField.textStyle,
                  margins: {
                    ...selectedField.textStyle?.margins,
                    [id]: newVal,
                  },
                },
              });
            }
          }}
        >
          −
        </button>
        <input
          type="number"
          className={styles.marginInput}
          id={id}
          aria-describedby={id}
          value={value}
          onChange={(e) =>
            updateField({
              textStyle: {
                ...selectedField.textStyle,
                margins: {
                  ...selectedField.textStyle?.margins,
                  [id]: Number(e.target.value) || 0, // not undefined or NaN
                },
              },
            })
          }
        />
        <button
          className={toolbarStyles.increment}
          onClick={() =>
            updateField({
              textStyle: {
                ...selectedField.textStyle,
                margins: {
                  ...selectedField.textStyle?.margins,
                  [id]: (value ?? 0) + 1,
                },
              },
            })
          }
        >
          +
        </button>
      </div>
    </div>
  );

  const widthOptions = Array.from({ length: 10 }, (_, i) => {
    const percent = (i + 1) * 10;
    return {
      value: percent,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "60px",
              height: "8px",
              background: "#e0e0e0",
              position: "relative",
              borderRadius: "4px",
            }}
          >
            <div
              style={{
                width: `${percent}%`,
                height: "100%",
                background: "var(--theme1_primary)",
                borderRadius: "4px",
              }}
            />
          </div>
          <span>{percent}%</span>
        </div>
      ),
    };
  });

  const directionOptions = [
    { value: "top", label: "↑ Top" },
    { value: "left", label: "← Left" },
    { value: "right", label: "→ Right" },
    { value: "bottom", label: "↓ Bottom" },
  ];

  return (
    <>
      {/* Styler buttons */}
      <div className={styles.textStyleControls}>
        <div className={styles.textStyleButtons}>
          <button
            className={
              selectedField.textStyle?.bold
                ? styles.activeStyle
                : styles.inactiveStyle
            }
            onClick={() => toggleStyle("bold")}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            className={
              selectedField.textStyle?.italic
                ? styles.activeStyle
                : styles.inactiveStyle
            }
            onClick={() => toggleStyle("italic")}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            className={
              selectedField.textStyle?.underline
                ? styles.activeStyle
                : styles.inactiveStyle
            }
            onClick={() => toggleStyle("underline")}
            title="Underline"
          >
            <u>U</u>
          </button>
          <button
            className={
              selectedField.textStyle?.strikethrough
                ? styles.activeStyle
                : styles.inactiveStyle
            }
            onClick={() => toggleStyle("strikethrough")}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
        </div>

        {/* Color Picker */}
        <div className={styles.fontColorInput}>
          <input
            type="color"
            value={selectedField.textStyle?.color || "#000000"}
            onChange={(e) =>
              updateField({
                textStyle: {
                  ...selectedField.textStyle,
                  color: e.target.value,
                },
              })
            }
            className={styles.colorPicker}
            title="Font Color"
          />
        </div>
      </div>
      <Row className={`no-gutter ${styles.labelMarginWrapper}`}>
        {renderLabelMarginInput(
          "Top",
          "top",
          selectedField?.textStyle?.margins?.top
        )}
        {renderLabelMarginInput(
          "Right",
          "right",
          selectedField?.textStyle?.margins?.right
        )}
        {renderLabelMarginInput(
          "Bottom",
          "bottom",
          selectedField?.textStyle?.margins?.bottom
        )}
        {renderLabelMarginInput(
          "Left",
          "left",
          selectedField?.textStyle?.margins?.left
        )}
      </Row>
      {/* Field Width */}
      <div className={toolbarStyles.formGroup}>
        <label>Label Width (%)</label>
        <Select
          options={widthOptions}
          value={widthOptions.find(
            (opt) => opt.value === selectedField.textStyle?.width
          )}
          onChange={(option) => {
            if (option) {
              updateField({
                textStyle: {
                  ...selectedField.textStyle,
                  width: option.value,
                },
              });
            }
          }}
          placeholder="Select width"
          classNamePrefix="react-select"
          className="custom-select"
          menuPortalTarget={document.body}
          menuPlacement="top"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }), // optional but helps
          }}
        />
      </div>

      <div className={styles.textStyleContainer}>
        {/* Font size input */}
        <div className={styles.fontSizeInputContainer}>
          <label style={{ fontWeight: 500, fontSize: 8 }}>Font Size</label>
          <div className={toolbarStyles.customNumberInput}>
            <button
              className={toolbarStyles.decrement}
              onClick={() =>
                selectedField.textStyle?.fontSize &&
                updateField({
                  textStyle: {
                    ...selectedField.textStyle,
                    fontSize: Number(selectedField.textStyle?.fontSize - 1),
                  },
                })
              }
            >
              −
            </button>
            <input
              type="number"
              className={styles.fontSizeInput}
              value={selectedField.textStyle?.fontSize ?? 14}
              onChange={(e) =>
                updateField({
                  textStyle: {
                    ...selectedField.textStyle,
                    fontSize: Number(e?.target.value),
                  },
                })
              }
              title="Font Size"
              placeholder="Font size"
            />
            <button
              className={toolbarStyles.increment}
              onClick={() =>
                selectedField.textStyle?.fontSize &&
                updateField({
                  textStyle: {
                    ...selectedField.textStyle,
                    fontSize: Number(selectedField.textStyle?.fontSize + 1),
                  },
                })
              }
            >
              +
            </button>
          </div>
        </div>
        {/* Label Postion Container */}
        <div className={toolbarStyles.formGroup}>
          <label style={{ fontWeight: 500, fontSize: 8 }}>Label Position</label>
          <Select
            options={directionOptions}
            value={directionOptions.find((opt) => opt.value === selectedField.labelPosition)}
            onChange={(selected) => {
              if (selected) {
                updateField({
                  ...selectedField,
                  labelPosition: selected.value as
                    | "top"
                    | "left"
                    | "right"
                    | "bottom",
                });
              }
            }}
            placeholder="Select direction"
            menuPlacement="top"
            classNamePrefix="react-select"
            className="custom-select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
      </div>
    </>
  );
};

export default FieldLabelStyler;
